import type { MediaItem } from '@fdpf/shared';

export interface SlideshowCallbacks {
  onIndexChange?(index: number, item: MediaItem): void;
  onPausedChange?(paused: boolean): void;
  onItemFailed?(item: MediaItem, error: unknown): void;
  onAllFailed?(): void;
  /**
   * Fired when the engine changes the mute state by itself — i.e. the browser refused unmuted
   * autoplay and we fell back to muted so the frame keeps running instead of stalling.
   */
  onMutedChange?(muted: boolean, forced: boolean): void;
}

export interface SlideshowOptions extends SlideshowCallbacks {
  intervalMs: number;
  /**
   * Videos start muted because no browser allows unmuted autoplay without a user gesture — an
   * unmuted frame would simply stop advancing. The viewer exposes a toggle, and unmuting from a real
   * tap is permitted; if it is ever refused anyway we fall back to muted rather than freeze.
   */
  muted: boolean;
}

type Prepared =
  | { kind: 'photo'; img: HTMLImageElement }
  | { kind: 'video'; url: string; bytes: number };

/**
 * How many items ahead to prepare. Two is deliberate: enough to hide a slow decode, few enough that
 * an old iPad with 1 GB of RAM never holds more than a handful of full-size bitmaps. Safari on those
 * devices kills the tab rather than degrading when it runs out.
 */
const PREPARE_AHEAD = 2;
const MAX_PREPARED = 4;

/** Videos larger than this are not pre-fetched; we take the stall rather than risk a memory spike. */
const MAX_PREFETCH_VIDEO_BYTES = 48 * 1024 * 1024;

export function mediaUrl(contentHash: string, variant: 'display' | 'thumb' | 'poster'): string {
  return `/m/${contentHash}/${variant}`;
}

/** Serves the archived ORIGINAL, with Content-Disposition set so the browser saves it. */
export function downloadUrl(contentHash: string): string {
  return `/d/${contentHash}`;
}

export class Slideshow {
  #container: HTMLElement;
  #items: MediaItem[];
  #options: SlideshowOptions;

  #layers: [HTMLImageElement, HTMLImageElement];
  #video: HTMLVideoElement;
  #activeLayer = 0;

  #prepared = new Map<string, Prepared>();
  #failed = new Set<string>();
  #index = 0;
  #paused = false;
  #muted = true;
  #timer: number | null = null;
  #destroyed = false;
  /** Guards against a slow load finishing after the user has already moved on. */
  #generation = 0;

  constructor(container: HTMLElement, items: MediaItem[], options: SlideshowOptions) {
    this.#container = container;
    this.#items = items;
    this.#options = options;

    const makeLayer = () => {
      const img = document.createElement('img');
      img.className = 'slide-layer';
      img.alt = '';
      // Decoding on the main thread is what makes old iPads stutter during a crossfade.
      img.decoding = 'async';
      return img;
    };

    this.#layers = [makeLayer(), makeLayer()];
    this.#video = document.createElement('video');
    this.#video.className = 'slide-layer slide-video';
    this.#video.setAttribute('playsinline', '');
    this.#video.setAttribute('webkit-playsinline', '');
    this.#video.playsInline = true;
    this.#video.preload = 'auto';

    this.#muted = options.muted;
    this.#applyMuted();

    this.#video.addEventListener('ended', this.#onVideoEnded);
    this.#video.addEventListener('error', this.#onVideoError);

    container.appendChild(this.#layers[0]);
    container.appendChild(this.#layers[1]);
    container.appendChild(this.#video);
  }

  get index(): number {
    return this.#index;
  }

  get paused(): boolean {
    return this.#paused;
  }

  get muted(): boolean {
    return this.#muted;
  }

  /**
   * Must be driven by a real user gesture when unmuting — that is what makes the browser allow it.
   * If playback is refused anyway, `#playVideo` falls back to muted rather than leaving a dead frame.
   */
  setMuted(muted: boolean): void {
    if (this.#muted === muted) return;
    this.#muted = muted;
    this.#applyMuted();
    this.#options.onMutedChange?.(muted, false);

    // Re-kick playback so an unmute takes effect on the clip already on screen.
    if (!this.#paused && this.currentItem?.kind === 'video') this.#playVideo(this.currentItem);
  }

  #applyMuted(): void {
    this.#video.muted = this.#muted;
    // Older WebKit only consults the attribute form when deciding whether autoplay is allowed, so
    // both have to be kept in sync.
    if (this.#muted) this.#video.setAttribute('muted', '');
    else this.#video.removeAttribute('muted');
  }

  get currentItem(): MediaItem | undefined {
    return this.#items[this.#index];
  }

  async start(index = 0): Promise<void> {
    this.#index = this.#clampIndex(index);
    await this.#show(this.#index);
  }

  destroy(): void {
    this.#destroyed = true;
    this.#clearTimer();
    this.#video.removeEventListener('ended', this.#onVideoEnded);
    this.#video.removeEventListener('error', this.#onVideoError);
    this.#video.pause();
    this.#video.removeAttribute('src');
    this.#video.load();
    for (const key of [...this.#prepared.keys()]) this.#evict(key);
    for (const layer of this.#layers) {
      layer.removeAttribute('src');
      layer.remove();
    }
    this.#video.remove();
  }

  setIntervalMs(intervalMs: number): void {
    this.#options.intervalMs = intervalMs;
    // Reschedule so a changed interval takes effect on the current slide, not the next one.
    if (!this.#paused && this.currentItem?.kind === 'photo') this.#scheduleAdvance();
  }

  setPaused(paused: boolean): void {
    if (this.#paused === paused) return;
    this.#paused = paused;
    this.#options.onPausedChange?.(paused);

    if (paused) {
      this.#clearTimer();
      if (this.currentItem?.kind === 'video') this.#video.pause();
    } else {
      const item = this.currentItem;
      if (item?.kind === 'video') this.#playVideo(item);
      else this.#scheduleAdvance();
    }
  }

  next(): void {
    void this.#step(1);
  }

  prev(): void {
    void this.#step(-1);
  }

  goTo(index: number): void {
    void this.#show(this.#clampIndex(index));
  }

  // --- internals ------------------------------------------------------------

  #clampIndex(index: number): number {
    if (this.#items.length === 0) return 0;
    const n = this.#items.length;
    return ((index % n) + n) % n;
  }

  #clearTimer(): void {
    if (this.#timer !== null) {
      clearTimeout(this.#timer);
      this.#timer = null;
    }
  }

  #scheduleAdvance(): void {
    this.#clearTimer();
    if (this.#paused || this.#destroyed) return;
    this.#timer = setTimeout(() => void this.#step(1), this.#options.intervalMs) as unknown as number;
  }

  async #step(delta: 1 | -1): Promise<void> {
    if (this.#destroyed || this.#items.length === 0) return;
    await this.#show(this.#clampIndex(this.#index + delta), delta);
  }

  #onVideoEnded = (): void => {
    if (!this.#paused) void this.#step(1);
  };

  #onVideoError = (): void => {
    const item = this.currentItem;
    if (item) {
      this.#failed.add(item.contentHash);
      this.#options.onItemFailed?.(item, new Error('视频播放失败'));
    }
    if (!this.#paused) void this.#step(1);
  };

  /**
   * Renders the item at `index`. If it cannot be loaded, it is marked failed and the next one in the
   * same direction is tried — a single corrupt object must not freeze the frame forever.
   */
  async #show(index: number, direction: 1 | -1 = 1): Promise<void> {
    if (this.#destroyed || this.#items.length === 0) return;

    this.#clearTimer();
    const generation = ++this.#generation;

    let target = index;
    for (let attempts = 0; attempts < this.#items.length; attempts++) {
      const item = this.#items[target];
      if (!item) return;

      if (this.#failed.has(item.contentHash)) {
        target = this.#clampIndex(target + direction);
        continue;
      }

      try {
        const prepared = await this.#prepare(item);
        if (this.#destroyed || generation !== this.#generation) return;

        this.#index = target;
        this.#render(prepared, item);
        this.#options.onIndexChange?.(target, item);
        this.#prefetchAhead(target);
        return;
      } catch (err) {
        this.#failed.add(item.contentHash);
        this.#options.onItemFailed?.(item, err);
        target = this.#clampIndex(target + direction);
      }
    }

    this.#options.onAllFailed?.();
  }

  #render(prepared: Prepared, item: MediaItem): void {
    if (prepared.kind === 'photo') {
      this.#video.pause();
      this.#video.classList.remove('is-visible');

      const nextLayer = this.#activeLayer === 0 ? 1 : 0;
      const incoming = this.#layers[nextLayer]!;
      const outgoing = this.#layers[this.#activeLayer]!;

      incoming.src = prepared.img.src;
      incoming.classList.add('is-visible');
      outgoing.classList.remove('is-visible');
      this.#activeLayer = nextLayer;

      this.#scheduleAdvance();
      return;
    }

    // Video: hide both photo layers and hand over to the single reused <video>.
    for (const layer of this.#layers) layer.classList.remove('is-visible');
    this.#video.src = prepared.url;
    this.#video.classList.add('is-visible');
    this.#video.currentTime = 0;
    if (!this.#paused) this.#playVideo(item);
  }

  /**
   * Starts the current clip, degrading rather than stalling.
   *
   * A frame left unmuted will eventually meet a browser that refuses to autoplay with sound (a fresh
   * page load with no recent gesture is the common case). Silently forcing mute and carrying on is
   * far better than a frame that stops advancing until somebody walks over and taps it.
   */
  #playVideo(item: MediaItem): void {
    void this.#video.play().catch(() => {
      if (!this.#muted) {
        this.#muted = true;
        this.#applyMuted();
        this.#options.onMutedChange?.(true, true);
        void this.#video.play().catch(() => this.#giveUpOn(item));
        return;
      }
      this.#giveUpOn(item);
    });
  }

  #giveUpOn(item: MediaItem): void {
    this.#failed.add(item.contentHash);
    this.#options.onItemFailed?.(item, new Error('这段视频无法播放，已跳过'));
    void this.#step(1);
  }

  async #prepare(item: MediaItem): Promise<Prepared> {
    const cached = this.#prepared.get(item.contentHash);
    if (cached) return cached;

    let prepared: Prepared;
    if (item.kind === 'photo') {
      prepared = { kind: 'photo', img: await loadImage(mediaUrl(item.contentHash, 'display')) };
    } else {
      // Fetched into a Blob rather than pointed at with <video src>: Safari's media cache for
      // byte-range requests is small and unreliable, so a looping frame would re-download the clip
      // every cycle. A blob plays entirely from memory, and once the Service Worker has the object
      // cached this fetch never touches the network at all.
      const res = await fetch(mediaUrl(item.contentHash, 'display'), { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`视频下载失败 (HTTP ${res.status})`);
      const blob = await res.blob();
      prepared = { kind: 'video', url: URL.createObjectURL(blob), bytes: blob.size };
    }

    this.#prepared.set(item.contentHash, prepared);
    this.#trim();
    return prepared;
  }

  #prefetchAhead(fromIndex: number): void {
    for (let offset = 1; offset <= PREPARE_AHEAD; offset++) {
      const item = this.#items[this.#clampIndex(fromIndex + offset)];
      if (!item || this.#failed.has(item.contentHash) || this.#prepared.has(item.contentHash)) continue;
      if (item.kind === 'video' && item.bytesDisplay > MAX_PREFETCH_VIDEO_BYTES) continue;
      // Failures here are not interesting: #show will surface them when it reaches the item.
      void this.#prepare(item).catch(() => {});
    }
  }

  /** Keeps memory bounded by evicting the entries furthest from the current position. */
  #trim(): void {
    if (this.#prepared.size <= MAX_PREPARED) return;

    const distance = (hash: string): number => {
      const index = this.#items.findIndex((i) => i.contentHash === hash);
      if (index === -1) return Number.MAX_SAFE_INTEGER;
      const forward = this.#clampIndex(index - this.#index);
      const backward = this.#clampIndex(this.#index - index);
      return Math.min(forward, backward);
    };

    const keys = [...this.#prepared.keys()].sort((a, b) => distance(b) - distance(a));
    const currentHash = this.currentItem?.contentHash;
    for (const key of keys) {
      if (this.#prepared.size <= MAX_PREPARED) break;
      if (key === currentHash) continue;
      this.#evict(key);
    }
  }

  #evict(hash: string): void {
    const entry = this.#prepared.get(hash);
    if (!entry) return;
    if (entry.kind === 'video') {
      URL.revokeObjectURL(entry.url);
    } else {
      // Dropping the src is what actually lets WebKit release the decoded bitmap.
      entry.img.removeAttribute('src');
    }
    this.#prepared.delete(hash);
  }
}

/**
 * `decode()` (Safari 11.1+) resolves once the bitmap is ready, so the crossfade cannot show a
 * half-painted image. Where it is missing we fall back to `onload`, which is close enough.
 */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = 'async';

    const done = () => {
      if (typeof img.decode === 'function') {
        img.decode().then(
          () => resolve(img),
          // A decode failure on an image that loaded is usually harmless; show it anyway.
          () => resolve(img),
        );
      } else {
        resolve(img);
      }
    };

    img.onload = done;
    img.onerror = () => reject(new Error('图片加载失败: ' + src));
    img.src = src;
  });
}
